import React, { cloneElement, useEffect, useRef, useState } from 'react'
import * as Dialog from "@radix-ui/react-dialog";
import { useTranslation } from 'react-i18next';
import { X } from "lucide-react";
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { IconButton } from '../common/icon-button';
import { agentManager, agentsHooks, iamAccountHooks, iamAccountManager, iamTokenManager } from '@/manager';
import { AvatarUpload } from './avatar-upload';
import { Divider } from '../common/divider';
import { SubNameForm } from './sub-name-form';
import EmailForm from './email-form';
import { LanguageForm } from './language-form';
import { AccountIcon } from '../settings/assets';
import { SwitchHorizontalIcon } from '../agent-creation/assets/switch-horizontal-icon';
import { Image } from '../common/image';
import { useI18nNavigate } from '@/hooks/use-i18n-navigate';
import { AgentData } from '@/services/agent-list';
import { Spinner } from '../ui/modal';
import { ENV } from '@/constant';
import { isAgentAvailableForUser } from '@/utils/agent-availability';
import { AgentResolveData } from '@/services/agent-resolve';

interface SettingsAccountModalProps {
  trigger?: React.ReactElement;
  open?: boolean;
  loading?: boolean;
  agent?: AgentData;
  isSubscribed: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** 设置账户弹窗 */
export const SettingsAccountModal: React.FC<SettingsAccountModalProps> = ({
  trigger,
  open: externalOpen,
  agent,
  isSubscribed,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { toast } = useToast();
  // 智能体列表
  const { agents, loading } = agentsHooks.useAgentData();
  // 当前智能体信息
  const { currentAgent, resolvedAgent } = agentsHooks.useGetCurrentAgent();
  const navigate = useI18nNavigate();

  const { account } = iamAccountHooks.useData();

  // 判断是否使用 trigger 模式（trigger 优先）
  const isTriggerMode = !!trigger;

  // 根据模式决定使用哪个 open 状态
  const currentOpen = isTriggerMode ? internalOpen : externalOpen ?? false;

  // 处理 open 状态变化
  const handleOpenChange = (open: boolean) => {
    if (isTriggerMode) {
      setInternalOpen(open);
    } else {
      onOpenChange?.(open);
    }
  };


  // 关闭
  const handleClose = () => {
    handleOpenChange(false);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  // 弹窗打开时，强制聚焦到标题
  useEffect(() => {
    if (currentOpen) {
      // 等待DOM更新后执行聚焦（确保元素已挂载）
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    } else {
      setActiveTab('account');
    }
  }, [currentOpen]);

  // 切换系统（跳转到定制域名, 现在只有测试环境和开发环境,才会有切换系统功能）
  const handleSelect = (item: AgentData): boolean => {
    const shareToken = iamTokenManager.getToken();
    if (ENV.isProdSubEnv || ENV.isGraySubEnv) {
      if (!item.subDomain) {
        toast.default({
          description: t('select_system.tips'),
        })
        return false;
      }
      const domain = item.subDomain.replace(/\/+$/, '');
      agentManager.setAgentIdToCache(item.id, item.subDomain)
      location.href = `${domain}/agent/creation/${item.id}/?shareToken=${shareToken}&targetAgentId=${item.id}`
    } else {
      // 测试环境, 优先使用定制域名, 兜底用测试环境域名
      if (ENV.isTestMainEnv || ENV.isTestSubEnv) {
        const domain = item.subDomain ? item.subDomain?.replace(/\/+$/, '') : '';
        agentManager.setAgentIdToCache(item.id, domain || ENV.testMainEnvOrigin)
        location.href = `${domain || ENV.testMainEnvOrigin}/agent/creation/${item.id}/?shareToken=${shareToken}&targetAgentId=${item.id}`
        return;
      }
      // 开发环境，只跳本地路由
      agentManager.setAgentIdToCache(item.id, location.origin)
      location.href = `${location.origin}/agent/creation/${item.id}/?shareToken=${shareToken}&targetAgentId=${item.id}`
    }
    return true;
  };

  const tabs = [
    {
      key: 'account',
      title: t("settings_account.index.account"),
      icon: <AccountIcon className='size-5 text-[rgba(13,13,13,1)]' />
    },
    {
      key: 'system',
      title: t('select_system.switch_system'),
      icon: <SwitchHorizontalIcon className='size-5 text-[rgba(13,13,13,1)]' />,
      hidden: ENV.isProdSubEnv || ENV.isGraySubEnv, // 线上定制域名，关闭切换系统功能（开发和测试环境开启是因为方便调试）
    },
  ]

  const dialogContent = (
    <Dialog.Portal>
      <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
      <Dialog.Content
        className={cn(
          "flex flex-col",
          "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
          "bg-white rounded-[1.25rem] shadow-lg",
          "w-[700px] max-h-[610px] min-h-[610px] flex-shrink-0",
          "z-50 overflow-hidden"
        )}>
        {/* 标题区域 */}
        <div className="flex max-h-[92px] justify-between items-center px-6 pt-5 pb-5">
          <Dialog.Title
            ref={titleRef}
            tabIndex={-1}
            className='focus:outline-none text-[rgba(24,29,39,1)] text-lg font-medium flex items-center'
          >
            <span>{t("settings_account.title")}</span>
          </Dialog.Title>
          <Dialog.Close asChild>
            <IconButton
              className='size-8'
              icon={<X className="size-6 text-[rgba(16,24,40,1)]" />}
              onClick={handleClose}
            />
          </Dialog.Close>
        </div>
        {/* 内容 */}
        <div className='flex-1 flex pl-3 pr-6 min-h-0'>
          <div className='flex flex-col mr-6 gap-0.5'>
            {tabs?.filter(tab => !tab.hidden).map((tab, index) => (
              <div
                key={index}
                className={cn(
                  'w-[180px] py-2 px-3 flex items-center  rounded-[6px] gap-2 cursor-pointer hover:bg-[rgba(16,24,40,0.05)] transition-all',
                  activeTab === tab.key && 'bg-[rgba(16,24,40,0.05)]'
                )}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon}
                <span className='text-sm text-[rgba(13,13,13,1)]'>{tab.title}</span>
              </div>
            ))}
          </div>
          <div className='flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden'>
            {/* 账户信息 */}
            {activeTab === 'account' && (
              <AccountSection
                account={account}
                agent={agent}
                isSubscribed={isSubscribed}
              />
            )}
            {/* 切换系统 */}
            {activeTab === 'system' && (
              <SystemSection
                agents={agents}
                currentAgent={resolvedAgent}
                onSwitch={handleSelect}
              />
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )

  // trigger 模式
  if (isTriggerMode) {
    return (
      <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              handleTriggerClick();
              // 保留原有的 onClick 事件
              if (trigger.props.onClick) {
                trigger.props.onClick(e);
              }
            },
          })}
        </Dialog.Trigger>
        {dialogContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={currentOpen} onOpenChange={handleOpenChange}>
      {dialogContent}
    </Dialog.Root>
  );
}

// 账户设置内容组件
const AccountSection: React.FC<{
  account: ReturnType<typeof iamAccountManager.get>;
  agent?: AgentData;
  isSubscribed?: boolean;
}> = ({ account, agent, isSubscribed }) => {
  const { save } = agentsHooks.useAgentUserinfoSave();
  const { upload: uploadAvatar } = agentsHooks.useUploadUserAvatar();

  const persistAgentUserInfo = async (patch: { subName?: string; subAvatar?: string }) => {
    const nextName = patch.subName ?? agent?.subName ?? "";
    const nextAvatar = patch.subAvatar ?? agent?.subAvatar ?? "";
    try {
      await save({
        subName: nextName,
        subIntro: agent?.subIntro ?? "",
        subAvatar: nextAvatar,
        subIntroduction: agent?.subIntroduction ?? "",
      });
      return true;
    } catch {
      return false;
    }
  };

  const handleAvatarUpload = async (file: File) => {
    const res = await uploadAvatar(file);
    if (res) {
      await persistAgentUserInfo({ subAvatar: res });
    }
  };

  const handleSubNameChange = async (name: string) => persistAgentUserInfo({ subName: name });

  return (
    <div className="flex-1 overflow-y-auto min-h-0 pr-1">
      {isSubscribed && (
        <>
          <AvatarUpload
            avatar={agent?.subAvatar}
            title={agent?.subName || "U"}
            onUpload={handleAvatarUpload}
          />
          <Divider />
          <SubNameForm subName={agent?.subName ?? ""} onChange={handleSubNameChange} />
          <Divider />
        </>
      )}
      <EmailForm email={account.email} />
      <Divider />
      <LanguageForm />
    </div>
  );
};

/** 切换系统区域 */
const SystemSection: React.FC<{
  agents?: ReturnType<typeof agentsHooks.useAgentData>['agents'];
  currentAgent: AgentData | AgentResolveData,
  onSwitch?: (agent: AgentData) => boolean;
}> = ({
  agents,
  currentAgent,
  onSwitch
}) => {
    const { t } = useTranslation();
    const filteredAgents = agents?.filter(item => item.id !== currentAgent?.id);
    const hasDifferentAgent = filteredAgents?.length > 0;
    return (
      <div className='flex-1 flex flex-col min-h-0 w-full overflow-hidden pb-2'>
        <div className='flex-shrink-0'>
          <div className='text-sm text-[rgba(152,162,179,1)] mb-4'>{t('select_system.current_system')}</div>
          <SystemItem
            isCurrent
            systemName={currentAgent.name}
            userName={(currentAgent as AgentData).creatorName}
            imageUrl={currentAgent.avatar}
          />
          {hasDifferentAgent && <Divider className="my-5" />}
        </div>

        {hasDifferentAgent && (
          <div className='flex-1 overflow-y-auto scrollbar-hide'>
            <div className='text-sm text-[rgba(152,162,179,1)] mb-4 flex-shrink-0'>
              {t('select_system.other_system')}
            </div>
            <div className='flex flex-col gap-6 pb-2'>
              {filteredAgents?.map((item, index) => {
                const tags: string[] = [];
                const isExpired = item.expireAt && Date.now() > item.expireAt * 1000
                const canUse = isAgentAvailableForUser(item)
                if (!canUse) {
                  tags.push(t('select_system.deactivate'));
                }
                if (isExpired) {
                  tags.push(t('select_system.expired'));
                }
                return (
                  <SystemItem
                    key={index}
                    systemName={item.name}
                    userName={item.creatorName}
                    imageUrl={item.avatar}
                    tags={tags}
                    showSwitch={canUse}
                    onSwitch={() => onSwitch?.(item)}
                  />
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

const SystemItem: React.FC<{
  systemName: string;
  userName?: string;
  imageUrl?: string;
  tags?: string[];
  isCurrent?: boolean;
  showSwitch?: boolean;
  onSwitch?: () => boolean;
}> = ({
  systemName,
  userName,
  imageUrl,
  tags,
  isCurrent,
  showSwitch,
  onSwitch,
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    return (
      <div className='flex justify-between items-center gap-2'>
        <div className='flex min-w-0'>
          <div className='size-[38px] rounded-full overflow-hidden mr-3 flex-shrink-0'>
            <Image
              src={imageUrl}
              className='w-full h-full flex-shrink-0'
            />
          </div>
          <div className='flex flex-col justify-center min-w-0'>
            <span className='text-sm text-[rgba(13,13,13,1)] truncate pb-[5px]'>{systemName}</span>
            <span className='text-xs text-[rgba(152,162,179,1)] truncate'>{userName}</span>
          </div>
          {tags?.map((tag, index) => (
            <span
              key={`${tag}-${index}`}
              className='text-xs ml-2 max-h-max text-[rgba(16,24,40,1)] px-2 py-1 bg-[rgba(16,24,40,0.05)] rounded-[6px] select-none flex-shrink-0'
            >
              {tag}
            </span>
          ))}
        </div>
        <div className='flex items-center gap-2 flex-shrink-0'>
          {isCurrent && (
            <span className='text-sm text-[rgba(16,24,40,1)] px-2 py-1 bg-[rgba(16,24,40,0.05)] rounded-[6px] select-none'>{t('select_system.current_usage')}</span>
          )}
          {showSwitch && (
            <div
              className='min-w-[61.6px] min-h-[37.6px] flex justify-center items-center text-sm font-medium text-[rgba(16,24,40,1)] py-2 px-4 border border-[rgba(208,213,221,1)] rounded-[8px] hover:bg-gray-50 cursor-pointer active:bg-gray-100 select-none'
              onClick={() => {
                if (loading) {
                  return;
                }
                const isSwitching = onSwitch?.();
                if (isSwitching) {
                  setLoading(true);
                }
              }}
            >
              {loading ? <Spinner className='size-4' /> : t('select_system.switch')}
            </div>
          )}
        </div>
      </div>
    )
  }